import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import ProjectState, {
  DocumentType,
  GraphView,
  Message,
  DocumentResultType,
  StructureType,
  Conversation,
  SearchType,
  Importance,
  Conclusion,
  DecisionLevel,
  CountryHUDOC,
  Article,
  CountryICJ,
  Filter,
  FilterHUDOC,
  FilterICJ,
} from '../ViewTypes';
import { Slice } from '../../api/APITypes';
import { cloneDeep } from 'lodash';
import deepMerge from '@/lib/DeepMerge';

export const defaultFilterOptions: Filter = {
  time: {
    upper: new Date(`${new Date().getFullYear() + 1}-01-01T00:00:00.000Z`).getTime(),
    lower: new Date('1945').getTime(),
  },
  source: {
    hudoc: {
      importance: Object.keys(Importance),
      conclusion: Object.keys(Conclusion),
      decisionLevel: Object.keys(DecisionLevel),
      countries: Object.keys(CountryHUDOC),
      articles: Object.keys(Article),
    },
    icj: {
      countries: Object.keys(CountryICJ)
    }
  }
};

export const initialProjectState: ProjectState = {
  id: uuidv4(),
  name: 'No Project',
  refreshCounter: 0,
  isAutosaveActive: false,
  toolOpenStatus: {
    search: false,
    savedCases: false,
    aiChat: false,
  },
  conversations: [
    {
      id: uuidv4(),
      name: 'New Chat',
      messages: [
        {
          issuer: 'system',
          content:
            'Hey, I am Meshy, your friendly virtual assistant. You can ask questions about the cases from the search results on the Homepage.',
          conversationId: '',
          datetime: new Date().getTime(),
        },
      ],
      datetime: new Date().getTime(),
    },
  ],
  openConversationId: '',
  documents: {
    filter: defaultFilterOptions,
    refreshCounter: 0,
    search: {
      type: 'CONTEXT',
      query: {
        CONTEXT: '',
        EXACT: '',
        DOCUMENT: '',
        GRAPH: '',
        FOLDER: '',
      },
    },
    result: {
      CONTEXT: {
        data: [],
        current_page: 0,
        current_page_elements: 0,
        total_pages: 0,
        total_elements: 0,
        page_size: 0,
      },
      EXACT: {
        data: [],
        current_page: 0,
        current_page_elements: 0,
        total_pages: 0,
        total_elements: 0,
        page_size: 0,
      },
    },
    structure: {
      GRAPH: {},
      FOLDER: {
        default: { id: 'default', name: 'Default', documents: [] },
      },
    },
  },
};

export const newInitialProjectState = (override: Partial<ProjectState>): ProjectState => {
  const newProjectState = cloneDeep(initialProjectState);

  newProjectState.id = uuidv4();

  return { ...newProjectState, ...override };
};

export const projectStateSlice = createSlice({
  name: 'projectState',
  initialState: initialProjectState,
  reducers: {
    setProjectState: (state, action: { payload: ProjectState }) => {
      deepMerge(state, action.payload);
    },
    addMessage: (state, action: { payload: Message }) => {
      let conversation = state.conversations.find(
        (conversation) => conversation.id === action.payload.conversationId,
      );
      if (!conversation) return;
      conversation.messages.push(action.payload);
    },
    clearConversation: (state, action: { payload: string }) => {
      let conversation = state.conversations.find(
        (conversation) => conversation.id === action.payload,
      );
      if (!conversation) return;
      conversation.messages = [];
    },
    renameConversation: (state, action: { payload: { id: string; name: string } }) => {
      let conversation = state.conversations.find(
        (conversation) => conversation.id === action.payload.id,
      );
      if (!conversation) return;
      conversation.name = action.payload.name;
    },
    addConversation: (state, action: { payload: Conversation }) => {
      state.conversations.push(action.payload);
    },
    removeConversation: (state, action: { payload: string }) => {
      const index = state.conversations.findIndex(
        (conversation) => conversation.id === action.payload,
      );
      if (index > -1 && state.conversations.length > 1) state.conversations.splice(index, 1);
    },
    setOpenConversationId: (state, action: { payload: string }) => {
      if (action.payload === state.openConversationId) return;
      if (!state.conversations.find((conversation) => conversation.id === action.payload)) return;
      state.openConversationId = action.payload;
    },
    addFloatingDoc: (
      state,
      action: {
        payload: {
          graphViewId: string;
          nodeId: string;
          id: string;
        };
      },
    ) => {
      let graphView = state.documents.structure.GRAPH[action.payload.graphViewId];
      let floatingDocumentIndex = graphView.floatingDocuments.findIndex(
        (doc) => doc.nodeId === action.payload.nodeId,
      );
      if (floatingDocumentIndex === -1)
        graphView.floatingDocuments.push({
          nodeId: action.payload.nodeId,
          id: action.payload.id,
        });
    },

    removeFloatingDoc: (
      state,
      action: { payload: { graphViewId: string; floatingDocId: string } },
    ) => {
      let graphView = state.documents.structure.GRAPH[action.payload.graphViewId];

      let floatingDocumentIndex = graphView.floatingDocuments.findIndex(
        (doc) => doc.id === action.payload.floatingDocId,
      );
      if (floatingDocumentIndex > -1) graphView.floatingDocuments.splice(floatingDocumentIndex, 1);
    },
    setCenterNode: (state, action: { payload: { graphViewId: string; centerNode: string } }) => {
      let graphView = state.documents.structure.GRAPH[action.payload.graphViewId];
      graphView.centerNode = action.payload.centerNode;
    },
    setTimeFilter: (
      state,
      action: {
        payload: {
          graphViewId: string;
          timeFilter: {
            upper: number;
            lower: number;
          };
        };
      },
    ) => {
      let graphView = state.documents.structure.GRAPH[action.payload.graphViewId];
      
      if (graphView.filter === undefined) {
        graphView.filter = defaultFilterOptions;
      }
      
      graphView.filter.time = action.payload.timeFilter;
    },

    addNewGraphView: (state, action: { payload: GraphView }) => {
      state.documents.structure.GRAPH[action.payload.id] = action.payload;
    },
    setToolOpenStatus: (
      state,
      action: {
        payload: {
          search: boolean;
          savedCases: boolean;
          aiChat: boolean;
        };
      },
    ) => {
      // Only allow one tool to be open at a time
      // If multiple are set to true, only the first one will be true
      if (action.payload.search) {
        action.payload.savedCases = false;
        action.payload.aiChat = false;
      } else if (action.payload.savedCases) {
        action.payload.search = false;
        action.payload.aiChat = false;
      } else if (action.payload.aiChat) {
        action.payload.search = false;
        action.payload.savedCases = false;
      }

      state.toolOpenStatus = action.payload;
    },
    incrementRefreshCounter: (state) => {
      state.refreshCounter++;
    },
    setDocumentFilterTime(
      state: ProjectState,
      action: {
        payload: {
          upper: number;
          lower: number;
        };
      },
    ) {
      const { upper, lower } = action.payload;
      state.documents.filter.time.upper = upper;
      state.documents.filter.time.lower = lower;
    },
    setDocumentFilterHUDOC<k extends keyof FilterHUDOC>(
      state: ProjectState,
      action: {
        payload: {
          type: k;
          value: FilterHUDOC[k];
        };
      },
    ) {
      const { type, value } = action.payload;
      state.documents.filter.source.hudoc[type] = value;
    },
    setDocumentFilterICJ<k extends keyof FilterICJ>(
      state: ProjectState,
      action: {
        payload: {
          type: k;
          value: FilterICJ[k];
        };
      },
    ) {
      const { type, value } = action.payload;
      state.documents.filter.source.icj[type] = value;
    },
    clearDocumentFilter: (state, action: { payload: boolean }) => {
      if (action.payload) {
        for (const key of Object.keys(state.documents.filter)) {
          (state.documents.filter as any)[key] = [];
        }
      }
    },
    resetDocumentFilter: (state, action: { payload: boolean }) => {
      if (action.payload) {
        for (const key of Object.keys(state.documents.filter)) {
          (state.documents.filter as any)[key] = (initialProjectState.documents.filter as any)[key];
        }
      }
    },
    incrementDocumentsRefreshCounter: (state) => {
      state.documents.refreshCounter++;
    },
    setDocumentSearchType(state, action: { payload: SearchType }) {
      state.documents.search.type = action.payload;
    },
    setDocumentSearchQuery(state, action: { payload: { type: DocumentType; query: string } }) {
      state.documents.search.query[action.payload.type] = action.payload.query;
    },
    setDocumentResult<K extends keyof DocumentResultType>(
      state: ProjectState,
      action: { payload: { type: K; value: Slice<DocumentResultType[K][]> } },
    ) {
      state.documents.result[action.payload.type] = action.payload.value;
    },

    setDocumentStructure<K extends keyof StructureType>(
      state: ProjectState,
      action: { payload: { type: K; key: string; value: StructureType[K] } },
    ) {
      const { type, key, value } = action.payload;

      state.documents.structure[type] = {
        ...state.documents.structure[type],
        [key]: value,
      };
    },
    removeDocumentStructure<K extends keyof StructureType>(
      state: ProjectState,
      action: { payload: { type: K; key: string } },
    ) {
      const { type, key } = action.payload;
      delete state.documents.structure[type][key];
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setProjectState,
  addMessage,
  clearConversation,
  addConversation,
  setOpenConversationId,
  renameConversation,
  removeConversation,
  addFloatingDoc,
  removeFloatingDoc,
  setCenterNode,
  setTimeFilter,
  addNewGraphView,
  setToolOpenStatus,
  incrementRefreshCounter,
  setDocumentFilterTime,
  setDocumentFilterHUDOC,
  setDocumentFilterICJ,
  incrementDocumentsRefreshCounter,
  clearDocumentFilter,
  resetDocumentFilter,
  setDocumentSearchType,
  setDocumentSearchQuery,
  setDocumentResult,
  setDocumentStructure,
  removeDocumentStructure,
} = projectStateSlice.actions;

export default projectStateSlice.reducer;
